import { useEffect, useState } from "react";
import { Activity, Clock3, LogOut, Settings2, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "./StatusBadge";
import { TokenCountdown } from "./TokenCountdown";
import { useLive } from "./LiveProvider";

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span data-testid="ist-clock" className="num text-xs text-slate-400">
      {now.toLocaleTimeString("en-IN", { hour12: false, timeZone: "Asia/Kolkata" })} IST
    </span>
  );
};

export const StatusBar = ({ health, auth, onSettings, onLogout, onDataMode }) => {
  const s = health?.stream;
  const authState = health?.auth_state;
  const live = useLive();
  return (
    <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-[#0B0F17]/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1720px] flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 sm:px-4 lg:px-6">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-cyan-400" />
          <span className="font-display text-lg font-bold tracking-tight text-slate-100">
            Kite<span className="text-cyan-400">//</span>Bridge
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="status-badges">
          {authState === "live_only" ? (
            <StatusBadge testid="badge-data-source" status="down" label="LIVE-ONLY · NO SESSION" />
          ) : (
            <StatusBadge testid="badge-data-source"
              status={health?.data_source !== "kite" ? "simulated" : s?.status === "connected" ? "connected" : "stale"}
              label={health?.data_source === "kite" ? "KITE FEED" : "SIMULATED FEED"} />
          )}
          {authState && !["simulated", "live_only"].includes(authState) && <StatusBadge testid="badge-auth" status={authState} />}
          {s && !["simulated", "auth_required", "off", "idle"].includes(s.status) && <StatusBadge testid="badge-stream" status={s.status} />}
          {health?.market && (
            <StatusBadge testid="badge-market" status={health.market.status}
              label={health.market.is_open ? "MARKET OPEN" : `CLOSED · ${health.market.reason}`.toUpperCase()} />
          )}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="hidden items-center gap-1.5 rounded border border-slate-800 bg-slate-900 px-2 py-1 md:flex" title="Allow simulated data when no Zerodha session">
            <span className="font-mono text-[10px] text-slate-400">{health?.allow_simulated ? "SIMULATED" : "LIVE"}</span>
            <Switch data-testid="data-mode-switch" checked={!!health?.allow_simulated} onCheckedChange={(v) => onDataMode(v)} />
          </label>
          {live && (
            <span data-testid="ws-indicator" title={live.connected ? "Live push connected" : "Live push reconnecting"}>
              {live.connected ? <Wifi className="h-4 w-4 text-emerald-400" /> : <WifiOff className="h-4 w-4 text-amber-400" />}
            </span>
          )}
          {auth?.expected_expiry && (
            <span className="hidden items-center gap-1 lg:flex" data-testid="header-token-countdown-wrap">
              <Clock3 className="h-3.5 w-3.5 text-slate-500" />
              <TokenCountdown expiry={auth.expected_expiry} testid="header-token-countdown" />
            </span>
          )}
          <span data-testid="subscription-count" className="hidden font-mono text-[11px] text-slate-500 sm:inline">
            SUBS <span className="text-slate-200">{s?.subscriptions ?? "—"}</span> · TICKS{" "}
            <span className="text-slate-200">{s?.ticks_total ?? "—"}</span>
          </span>
          <Clock />
          <Button data-testid="open-settings-button" size="sm" variant="outline" onClick={onSettings}
            className="h-8 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Settings
          </Button>
          <Button data-testid="logout-button" size="icon" variant="ghost" onClick={onLogout} className="h-8 w-8 text-slate-400 hover:text-slate-100">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};
